import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { MobileNav } from './MobileNav'
import { Outlet, useLocation } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { TaskDetail } from '@/components/tasks/TaskDetail'
import { SearchModal, useSearchModal } from '@/components/search/SearchModal'
import { NewProjectModal } from '@/components/project/NewProjectModal'
import { ShortcutsPanel } from '@/components/ui/ShortcutsPanel'
import { CopyToModal } from '@/components/ui/CopyToModal'
import { OnboardingTour } from '@/components/onboarding/OnboardingTour'
import { OnboardingWelcome } from '@/components/onboarding/OnboardingWelcome'
import { CelebrationOverlay } from '@/components/ui/CelebrationOverlay'
import { CompletionModal } from '@/components/tasks/CompletionModal'
import { ContextMenuProvider } from '@/components/ui/ContextMenu'
import { DesignLab } from '@/components/ui/DesignLab'
import { GradientMesh } from '@/components/ui/GradientMesh'
import { useToast } from '@/components/ui/Toast'
import { ReloadPrompt } from '@/components/ui/ReloadPrompt'
import { AnimatePresence, motion } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layers } from 'lucide-react'

function SectionPickerModal({ projectId, sections, onSelect, onClose }) {
  const opts = sections.filter((s) => s.projectId === projectId && !s.type)
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    function handle(e) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(i + 1, opts.length - 1)); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); return }
      if (e.key === 'Enter') { e.preventDefault(); if (opts[idx]) onSelect(opts[idx]); return }
    }
    window.addEventListener('keydown', handle, true)
    return () => window.removeEventListener('keydown', handle, true)
  }, [idx, opts, onSelect, onClose])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 6 }}
        transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
        className="bg-surface border border-border rounded-2xl shadow-2xl shadow-black/30 w-72 overflow-hidden"
      >
        <div className="px-4 pt-4 pb-2">
          <p className="text-[11px] font-semibold text-text-faint uppercase tracking-wider">Add task to section</p>
        </div>
        <div className="pb-2">
          {opts.map((s, i) => (
            <button
              key={s.id}
              onClick={() => onSelect(s)}
              onMouseEnter={() => setIdx(i)}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                i === idx ? 'bg-accent/10 text-accent' : 'text-text hover:bg-surface-raised'
              }`}
            >
              <Layers size={13} className="flex-shrink-0 opacity-50" />
              {s.name}
            </button>
          ))}
          {opts.length === 0 && (
            <p className="px-4 py-3 text-sm text-text-faint">No sections available</p>
          )}
        </div>
        <div className="px-4 py-2 border-t border-border">
          <p className="text-[10px] text-text-faint">↑↓ navigate · ↵ select · Esc cancel</p>
        </div>
      </motion.div>
    </motion.div>
  )
}

function KickedScreen({ project, onDismiss }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[600] flex flex-col items-center justify-center bg-bg"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className="flex flex-col items-center gap-5 max-w-sm text-center px-8"
      >
        {/* Big frowny face */}
        <span style={{ fontSize: 96, lineHeight: 1, userSelect: 'none' }}>☹️</span>

        <div className="flex flex-col gap-1.5">
          <p className="text-xl font-bold text-text" style={{ fontFamily: 'Helvetica Neue, sans-serif', letterSpacing: '-0.02em' }}>
            You've been removed from<br />
            <span className="text-accent">"{project.name}"</span>
          </p>
          <p className="text-sm text-text-muted" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            i guess they didn't like you
          </p>
        </div>

        <button
          onClick={onDismiss}
          className="mt-2 px-6 py-2.5 rounded-xl bg-surface-raised border border-border text-sm font-medium text-text-muted hover:text-text hover:border-border/80 transition-all active:scale-[0.97]"
        >
          Got it
        </button>
      </motion.div>
    </motion.div>
  )
}

const STALE_MS = 45 * 60 * 1000 // 45 minutes

export function AppShell() {
  const location = useLocation()
  const selectedTaskId = useAppStore((s) => s.selectedTaskId)
  const kickedFromProject = useAppStore((s) => s.kickedFromProject)
  const clearKickedFromProject = useAppStore((s) => s.clearKickedFromProject)
  const lastSyncAt = useAppStore((s) => s._lastSyncAt)
  const { undo, redo, setActiveView, sections } = useAppStore()
  const [isStale, setIsStale] = useState(false)
  const { open: searchOpen, setOpen: setSearchOpen } = useSearchModal()
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [copyToTaskId, setCopyToTaskId] = useState(null)
  const [sectionPicker, setSectionPicker] = useState(null) // { projectId } | null
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [designVariant, setDesignVariant] = useState(() => {
    try { return localStorage.getItem('prjct-design-variant') || 'default' } catch { return 'default' }
  })
  const isProjectView = location.pathname.startsWith('/project')
  // Mobile TopBar is 48px (single row) on plain pages, 88px (two rows) on project pages
  const mobileSheetTop = isProjectView ? '88px' : '48px'
  const toast = useToast()
  const keyBuf = useRef('')
  const detailPanelRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  useEffect(() => {
    const handler = (e) => setDesignVariant(e.detail)
    window.addEventListener('design-variant-change', handler)
    return () => window.removeEventListener('design-variant-change', handler)
  }, [])

  useEffect(() => {
    const handler = (e) => toast({ message: e.detail?.message || 'Something failed to save. Check your connection.' })
    window.addEventListener('prjct-write-error', handler)
    return () => window.removeEventListener('prjct-write-error', handler)
  }, [toast])

  // Show stale banner when data hasn't refreshed in 45+ minutes
  useEffect(() => {
    if (!lastSyncAt) return
    setIsStale(false)
    const remaining = STALE_MS - (Date.now() - lastSyncAt)
    if (remaining <= 0) { setIsStale(true); return }
    const t = setTimeout(() => setIsStale(true), remaining)
    return () => clearTimeout(t)
  }, [lastSyncAt])

  // Close task detail when clicking outside the panel.
  // Ignore events with no valid target (happens when file picker dialog closes and
  // returns focus to the window — browsers fire a synthetic mousedown with target=body
  // or target=null that would incorrectly close the panel mid-upload).
  useEffect(() => {
    if (!selectedTaskId) return
    const handler = (e) => {
      if (!e.target || e.target === document.body || e.target === document.documentElement) return
      if (e.target.closest('[data-floating-ui]')) return // portaled pickers/dropdowns
      if (detailPanelRef.current && !detailPanelRef.current.contains(e.target)) {
        useAppStore.getState().setSelectedTask(null)
      }
    }
    // Defer so the click that opened the panel doesn't immediately close it
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler) }
  }, [selectedTaskId])

  useEffect(() => {
    function handleKey(e) {
      const inInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable
      const meta = e.metaKey || e.ctrlKey

      // ⌘Z undo
      if (meta && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        const did = undo()
        if (did) toast({ message: 'Undone', action: { label: 'Redo', onClick: redo } })
        return
      }

      // ⌘⇧Z redo
      if (meta && (e.key === 'Z' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        const did = redo()
        if (did) toast({ message: 'Redone', action: { label: 'Undo', onClick: undo } })
        return
      }

      // ⌘⇧C — copy selected task to another project/section
      if (meta && e.shiftKey && e.key === 'C') {
        const { selectedTaskId: taskId } = useAppStore.getState()
        if (taskId) {
          e.preventDefault()
          setCopyToTaskId(taskId)
          return
        }
      }

      // ⌘⇧N — new task: show section picker if multiple sections, else create immediately
      if (meta && e.shiftKey && e.key === 'N') {
        const { activeProjectId, sections, addTask, setSelectedTask } = useAppStore.getState()
        if (activeProjectId) {
          e.preventDefault()
          const projectSections = sections.filter((s) => s.projectId === activeProjectId && !s.type)
          if (projectSections.length <= 1) {
            const target = projectSections[0] || sections.find((s) => s.projectId === activeProjectId)
            if (target) {
              const id = addTask({ title: 'New task', sectionId: target.id, projectId: activeProjectId })
              setSelectedTask(id)
            }
          } else {
            setSectionPicker({ projectId: activeProjectId })
          }
          return
        }
      }

      // ⌘⇧D — toggle complete on selected task (routes through approval flow if not already completed)
      if (meta && e.shiftKey && e.key === 'D') {
        const { selectedTaskId: taskId, tasks, toggleTask, initiateCompletion } = useAppStore.getState()
        if (taskId) {
          e.preventDefault()
          const task = tasks.find((t) => t.id === taskId)
          if (task) {
            if (task.completed) {
              toggleTask(taskId)
              toast({ message: 'Marked incomplete' })
            } else {
              initiateCompletion(taskId, null)
            }
          }
          return
        }
      }

      // ⌘/ or ? — shortcuts panel
      if ((meta && e.key === '/') || (!inInput && e.key === '?')) {
        e.preventDefault()
        setShortcutsOpen(true)
        return
      }

      // Any other ⌘/Ctrl combo is a system or browser shortcut (⌘C copy, ⌘N new window, ⌘1..5 tab switch, etc.).
      // Bail out so single-letter task shortcuts below don't hijack them.
      if (meta) return

      // Key buffer — runs first, before any early returns, so multi-char codes
      // are always accumulated even when a single-key shortcut also fires
      if (!inInput && e.key.length === 1) {
        keyBuf.current = (keyBuf.current + e.key.toLowerCase()).slice(-8)
        if (keyBuf.current.includes('lgn')) {
          keyBuf.current = ''
          navigate('/auth')
          return
        }
        if (keyBuf.current.includes('reset')) {
          keyBuf.current = ''
          localStorage.removeItem('prjct-onboarding-v1')
          window.location.reload()
          return
        }
      }

      if (inInput) return

      const { selectedTaskId: taskId, tasks, deleteTask, setSelectedTask } = useAppStore.getState()

      // Escape — close task detail
      if (e.key === 'Escape' && taskId) {
        setSelectedTask(null)
        return
      }

      // Delete / Backspace — delete selected task
      if ((e.key === 'Delete' || e.key === 'Backspace') && taskId) {
        e.preventDefault()
        const task = tasks.find((t) => t.id === taskId)
        if (task) {
          setSelectedTask(null)
          deleteTask(taskId)
          toast({ message: `"${task.title}" deleted`, action: { label: 'Undo', onClick: undo } })
        }
        return
      }

      // Number keys — switch view
      if (e.key === '1') setActiveView('list')
      if (e.key === '2') setActiveView('board')
      if (e.key === '3') setActiveView('timeline')
      if (e.key === '4') setActiveView('calendar')
      if (e.key === '5') setActiveView('dashboard')
    }

    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [undo, redo, toast, setActiveView])

  return (
    <ContextMenuProvider>
    <div className="flex h-full overflow-hidden bg-bg" style={designVariant === 'glass' ? { position: 'relative' } : undefined}>
      {designVariant === 'glass' && (
        <div style={{ position: 'absolute', inset: 0, zIndex: -1, pointerEvents: 'none' }}>
          <GradientMesh
            colors={['#000000', '#080808', '#0f0f0f']}
            distortion={7}
            swirl={0.18}
            speed={0.42}
            rotation={50}
            waveAmp={0.26}
            waveFreq={8}
            waveSpeed={0.14}
            grain={0.04}
          />
        </div>
      )}
      {/* Sidebar — desktop only */}
      {!isMobile && (
        <Sidebar onNewProject={() => setNewProjectOpen(true)} />
      )}

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <TopBar onSearchOpen={() => setSearchOpen(true)} />
        <div className="flex flex-1 min-w-0 overflow-hidden">
          {/* Main content */}
          <div
            className="flex-1 min-w-0 overflow-auto relative"
            style={isMobile ? { paddingBottom: 'calc(52px + env(safe-area-inset-bottom))' } : undefined}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="h-full"
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Task detail — desktop fixed overlay */}
          <AnimatePresence>
            {!isMobile && selectedTaskId && (
              <>
                {/* Detail panel — blur is localized to the card only, board stays clear */}
                <motion.div
                  ref={detailPanelRef}
                  key="detail-panel"
                  initial={{ x: 24, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 24, opacity: 0 }}
                  transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
                  style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 440, zIndex: 100 }}
                  className="bg-surface/85 backdrop-blur-2xl border-l border-white/[0.08] shadow-2xl shadow-black/60 overflow-hidden"
                >
                  <TaskDetail taskId={selectedTaskId} />
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Task detail — mobile bottom sheet */}
      {isMobile && (
        <AnimatePresence>
          {selectedTaskId && (
            <>
              <motion.div
                key="task-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-xl z-30"
                onClick={() => useAppStore.getState().setSelectedTask(null)}
              />
              <motion.div
                key="task-sheet"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
                className="fixed left-0 right-0 bottom-0 z-40 rounded-t-2xl overflow-hidden shadow-2xl flex flex-col"
                style={{ top: mobileSheetTop }}
              >
                {/* Drag handle */}
                <div className="flex justify-center pt-2.5 pb-1 bg-surface flex-shrink-0">
                  <div className="w-10 h-1 rounded-full bg-border" />
                </div>
                <TaskDetail taskId={selectedTaskId} />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      )}

      {/* Bottom nav — mobile only */}
      {isMobile && (
        <MobileNav
          onSearchOpen={() => setSearchOpen(true)}
          onNewProject={() => setNewProjectOpen(true)}
        />
      )}

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      <NewProjectModal open={newProjectOpen} onClose={() => setNewProjectOpen(false)} />
      <ShortcutsPanel open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      {copyToTaskId && (
        <CopyToModal
          taskIds={[copyToTaskId]}
          onClose={() => setCopyToTaskId(null)}
          onDone={() => toast({ message: 'Task copied' })}
        />
      )}
      <OnboardingTour />
      <OnboardingWelcome />
      <CelebrationOverlay />
      <CompletionModal />
      <DesignLab />

      <AnimatePresence>
        {sectionPicker && (
          <SectionPickerModal
            key="section-picker"
            projectId={sectionPicker.projectId}
            sections={sections}
            onSelect={(section) => {
              const { addTask, setSelectedTask } = useAppStore.getState()
              const id = addTask({ title: 'New task', sectionId: section.id, projectId: sectionPicker.projectId })
              setSelectedTask(id)
              setSectionPicker(null)
            }}
            onClose={() => setSectionPicker(null)}
          />
        )}
      </AnimatePresence>

      {/* Stale-data full-screen refresh prompt */}
      <AnimatePresence>
        {isStale && (
          <ReloadPrompt
            key="stale"
            title="This page is out of date"
            message="You've been away for a while, so some changes may not be showing. Reload to load the latest."
            buttonLabel="Reload now"
          />
        )}
      </AnimatePresence>

      {/* Kicked-from-project screen */}
      <AnimatePresence>
        {kickedFromProject && (
          <KickedScreen
            key="kicked"
            project={kickedFromProject}
            onDismiss={() => { clearKickedFromProject(); navigate('/home') }}
          />
        )}
      </AnimatePresence>
    </div>
    </ContextMenuProvider>
  )
}
